import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import Modal from 'components/core/Modal';
import ModalDialog from 'components/core/ModalDialog';
import ModalClose from 'components/core/ModalClose';
import Stack from 'components/core/Stack';
import Autocomplete from 'components/core/Autocomplete';
import Typography from 'components/core/Typography';
import Sheet from 'components/core/Sheet';
import Button from 'components/core/Button';
import ProcessingOverlay from "components/core/ProcessingOverlay";

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import evidenceActions from 'actions/evidenceActions';
import observationActions from 'actions/observationActions';

import EvidenceForm from './EvidenceForm';
import EvidenceDossier from './EvidenceDossier';

const EvidenceAddDialog = ({ 
    open,
    onClose,
    observationId,
    observationEvidence,
    mode = 'direct',
    onSelect,
    excludeEvidence = []
}) => {
    const dispatch = useDispatch();
    const [newEvidence, setNewEvidence] = useState(false);
    const [selectedEvidence, setSelectedEvidence] = useState(null);
    const [evidenceId, setEvidenceId] = useState(null);
    const evidences = useSelector(({ evidence }) => evidence.list);
    const user = useSelector(({ auth }) => auth.me);
    const isLoadingObservations = useSelector(({ observation }) => observation.isLoading);
    const isLoadingEvidences = useSelector(({ evidence }) => evidence.isLoading);

    useEffect(() => {
        if (evidenceId) {
            dispatch(evidenceActions.get(evidenceId)).then((result) => {
                setSelectedEvidence(result.value);
            });
        } else {
            setSelectedEvidence(null);
        }
    }, [evidenceId]);

    useEffect(() => {
        dispatch(evidenceActions.list({ }));
    }, [dispatch]);

    const isLoading = isLoadingObservations || isLoadingEvidences;

    if (isLoading) {
        <ProcessingOverlay enabled={isLoading} />
    }

    const handleClose = () => {
        setNewEvidence(false);
        setEvidenceId(null);
        setSelectedEvidence(null);
        onClose();
    }

    const noOptionComponent = () => {
        return (
            <li onClick={() => { setNewEvidence(true) }} style={{cursor: 'pointer'}}>
                <Stack direction="row">
                    <AddCircleOutlineIcon sx={{marginRight: 2, color: 'black'}}/>
                    <Typography>Add New Evidence</Typography>
                </Stack>
            </li>
        )
    }

    const handleAttach = () => {
        if (mode === 'form') {
            if (selectedEvidence && onSelect) {
                onSelect(selectedEvidence);
            }
        } else {
            dispatch(evidenceActions.attachToObservation(evidenceId, observationId)).then(() => {
                dispatch(evidenceActions.list({ createdBy: user.id }));
                dispatch(observationActions.get(observationId));
                dispatch(evidenceActions.listForObservation(observationId));
            }).then(() => {
                handleClose();
            });
        }
    }

    const handleSelect = (evidence) => {
        if (evidence && evidence.isNoOption) {
            setNewEvidence(true);
        } else if (evidence) {
            setEvidenceId(evidence.id);
        }
    }

    const handleSubmit = (evidence) => {
        if (mode === 'form') {
            if (evidence && onSelect) {
                onSelect(evidence);
            }
        } else {
            dispatch(evidenceActions.list({ createdBy: user.id }));
            dispatch(observationActions.get(observationId));
            dispatch(evidenceActions.listForObservation(observationId));
        }
        handleClose();
    }

    const filteredEvidences = evidences.filter(evidence => {
        if (observationEvidence && observationEvidence.length > 0) {
            if (observationEvidence.find(oe => oe.id === evidence.id)) {
                return false;
            }
        }
        if (excludeEvidence && excludeEvidence.length > 0) {
            if (excludeEvidence.find(ex => ex.id === evidence.id)) {
                return false;
            }
        }
        return true;
    });

    const evidenceOptions = [
        { isNoOption: true, label: 'Add New Evidence' },
        ...filteredEvidences
    ];

    return (
        <Modal open={open} onClose={handleClose}>
            <ModalDialog layout={'center'} sx={{overflow: 'scroll', width: '75vw'}}>
                <ModalClose/>
 
                {newEvidence ? (
                    <>
                        <EvidenceForm
                            observationId={mode === 'direct' ? observationId : null}
                            evidences={evidences}
                            onSubmit={handleSubmit}
                        />
                    </>
                ) : (
                    <Stack spacing={2} alignItems='center'>
                        <Typography variant="h4">Add Evidence</Typography>
                        <Autocomplete
                            placeholder="Select Evidence"
                            onChange={handleSelect}
                            options={evidenceOptions}
                            noOptionComponent={noOptionComponent()}
                            getOptionLabel={option => option.label}
                            sx={{ mb: 8, width: '50vw' }}
                        />
                        <EvidenceDossier evidence={selectedEvidence} />
                        <Sheet>
                            <Button onClick={handleAttach} disabled={!evidenceId}>
                                {mode === 'form' ? 'Select' : 'Attach'}
                            </Button>
                        </Sheet>
                    </Stack>
                )}
            </ModalDialog>
        </Modal>
    );
};

EvidenceAddDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    observationId: PropTypes.string,
    observationEvidence: PropTypes.array,
    mode: PropTypes.oneOf(['direct', 'form']),
    onSelect: PropTypes.func,
    excludeEvidence: PropTypes.array
};

EvidenceAddDialog.defaultProps = {
    mode: 'direct',
    observationEvidence: [],
    excludeEvidence: []
};

export default EvidenceAddDialog;